import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Lightbox } from '~/eds/src/components/Lightbox/Lightbox';
import { UserAction } from '~/eds/src/types/UserAction';
import { slices } from '~/redux/slices';

import { LibraryModelVersion } from '../../types';
import { useModelLibraryCart } from '../hooks';
import {
  getEntityTypeIconProps,
  getStatusTextProps,
  testIsModelPublished,
} from '../utils';
import { ModelSummary } from './ModelSummary';

interface Props {
  categoryModels?: LibraryModelVersion[];
  hideActions?: boolean;
}

export const ModelPreview = ({ categoryModels, hideActions }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { addModelToCart, removeModelFromCart } = useModelLibraryCart(intl);

  const model = useSelector(slices.xRayLibrary.selectors.selectModelPreview);
  const category = useSelector(slices.xRayLibrary.selectors.selectCategory);
  const modelsInCart = useSelector(slices.xRayLibrary.selectors.selectCart);
  const enableCarousel = useSelector(
    slices.xRayLibrary.selectors.selectCarousel,
  );

  const isInCart = Boolean(
    modelsInCart.find(
      (cartModel) => cartModel.libraryModelId === model?.libraryModelId,
    ),
  );
  const isPublished = model && testIsModelPublished(model);
  const modelActions = useMemo(() => {
    if (hideActions) {
      return [];
    }
    const actions: UserAction[] = [];
    if (model && !isInCart && !isPublished) {
      actions.push({
        icon: 'plus-circle' as const,
        iconPosition: 'left' as const,
        mode: 'chip',
        status: 'active',
        text: intl.formatMessage({
          id: 'ModelPreview.addModel',
          defaultMessage: 'Add Model',
          description: 'Button text to add model to cart',
        }),
        onClick: () => addModelToCart(model),
      });
    }
    if (model && isInCart) {
      actions.push({
        icon: 'trash' as const,
        iconPosition: 'left' as const,
        mode: 'chip',
        status: 'danger',
        text: intl.formatMessage({
          id: 'ModelPreview.remove',
          defaultMessage: 'Remove',
          description: 'Button text to remove model from cart',
        }),
        onClick: () => removeModelFromCart(model),
      });
    }
    return actions;
  }, [category, hideActions, isInCart, isPublished, model]);

  if (!model) {
    return null;
  }

  const index =
    categoryModels?.findIndex(
      (categoryModel) => categoryModel.libraryModelId === model.libraryModelId,
    ) ?? 0;

  const icon = getEntityTypeIconProps(model, intl);
  const statusText = getStatusTextProps(model, intl);

  return (
    <Lightbox
      actions={modelActions}
      content={
        model.sampleOutputs?.text || model.sampleOutputs?.dashboard?.buckets ? (
          <ModelSummary model={model} />
        ) : undefined
      }
      description={model.description}
      icon={icon}
      name="model preview lightbox"
      paginate={
        enableCarousel && categoryModels
          ? {
              label:
                category?.label ??
                intl.formatMessage({
                  id: 'ModelPreview.allCategories',
                  defaultMessage: 'All Categories',
                  description: 'Label for the category containing all models',
                }),
              pageIndex: index + 1,
              totalCount: categoryModels.length,
              onUpdatePageIndex: (pageIndex: number) => {
                dispatch(
                  slices.xRayLibrary.actions.setModelPreview(
                    categoryModels[pageIndex - 1],
                  ),
                );
              },
            }
          : undefined
      }
      statusText={statusText}
      title={model.label}
      onHide={() => {
        dispatch(slices.xRayLibrary.actions.setModelPreview(null));
      }}
    />
  );
};
